/**
 * Same-origin API client. The session is an httpOnly cookie, so every request just
 * needs credentials:'include'. A 401 means the session expired; App bounces to the
 * sign-in screen.
 */

export type PortalKey = 'skyrun' | 'stayterra' | 'elevated';

export interface Portal {
  key: PortalKey;
  name: string;
  tickets: boolean;
}

export interface Me {
  email: string;
  name: string | null;
  role: 'hq' | 'admin';
  portals: Portal[];
}

export interface AccountRow {
  userId: number;
  fullName: string | null;
  email: string;
  createdAt: string | null;
  numListings: number | null;
  numListingsEnabled: number | null;
  upsells: { eciLco: boolean; bookingBooster: boolean };
  numLiveLocks: number | null;
  opsLive: boolean;
  phoneNumberValue: string | null;
  voiceRouting: boolean;
  bffLive: boolean;
  isDemo: boolean;
}

export interface UsageRow {
  userId: number;
  fullName: string | null;
  email: string;
  autopilotMessages: number;
  dashboardMessages: number;
  offersSent: number;
  upsellsSold: number;
  revenueEarlyCheckinUsdCents: number;
  revenueLateCheckoutUsdCents: number;
  revenueExtraNightsUsdCents: number;
  revenueCustomUsdCents: number;
  /** Any upsell type the GTM aggregate does not break out. */
  revenueOtherUsdCents: number;
  revenueTotalUsdCents: number;
  avgResponseTimeMinutes: number | null;
  responseSampleSize: number;
}

export interface DailyPoint {
  date: string;
  autopilot: number;
  dashboard: number;
  offers: number;
}

/** Totals for the selected date range and location scope, computed server-side. */
export interface UsageTotals {
  upsellRevenueUsdCents: number;
  autopilotMessages: number;
  dashboardMessages: number;
  offersSent: number;
  upsellsSold: number;
  /** Sample-weighted across tenants. Null when nothing was measured in the range. */
  avgResponseTimeMinutes: number | null;
}

export interface UsageResponse {
  rows: UsageRow[];
  daily: DailyPoint[];
  totals: UsageTotals;
}

export type UpsellGranularity = 'day' | 'week' | 'month';
export const UPSELL_TYPE_KEYS = ['earlyCheckin', 'lateCheckout', 'extraNights', 'custom', 'other'] as const;
export type UpsellTypeKey = (typeof UPSELL_TYPE_KEYS)[number];

export interface UpsellTypeStats {
  offers: number;
  sold: number;
  revenueUsdCents: number;
}

export interface UpsellPoint {
  date: string;
  offers: number;
  sold: number;
  revenueUsdCents: number;
  byType: Record<UpsellTypeKey, UpsellTypeStats>;
}

export interface UpsellTrends {
  granularity: UpsellGranularity;
  series: UpsellPoint[];
  /** Revenue in USD cents per location, keyed by user id. */
  locations: Array<{ date: string; byUser: Record<string, number> }>;
  customItems: Array<{ name: string; sold: number; revenueUsdCents: number }>;
}

export interface TicketStage {
  id: string;
  label: string;
  displayOrder: number;
  isClosed: boolean;
}

export interface TicketPipeline {
  id: string;
  label: string;
  displayOrder: number;
  stages: TicketStage[];
}

export type SlaPriority = 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW';

export interface SlaInfo {
  priority: SlaPriority;
  prioritySource: 'field' | 'subject';
  responseDueAt: string;
  resolutionDueAt: string;
}

export interface TicketSubmitter {
  email: string;
  name: string | null;
}

export interface Ticket {
  id: string;
  subject: string | null;
  content: string | null;
  pipelineId: string | null;
  stageId: string | null;
  priority: string | null;
  firstAgentResponseAt: string | null;
  sla: SlaInfo | null;
  category: string | null;
  ownerId: string | null;
  ownerName: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  closedAt: string | null;
  sourceType: string | null;
  tags: string | null;
  url: string | null;
  submitters?: TicketSubmitter[];
}

export interface TicketAttachment {
  id: string;
  name: string;
  extension: string | null;
  type: string;
  previewable: boolean;
  size: number | null;
  width: number | null;
  height: number | null;
  emailId: string;
  sentAt: string | null;
}

export interface TicketsBoard {
  pipelines: TicketPipeline[];
  tickets: Ticket[];
  fetchedAt: string;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function get<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'include' });
  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body ? String((body as { error: unknown }).error) : res.statusText;
    throw new ApiError(res.status, message || 'Request failed');
  }
  return body as T;
}

function range(start: string, end: string): string {
  return `start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
}

export function ticketAttachmentUrl(ticketId: string, fileId: string): string {
  return `/api/tickets/${ticketId}/attachments/${fileId}`;
}

function ids(userIds?: number[]): string {
  return userIds?.length ? `&userIds=${userIds.join(',')}` : '';
}

export const api = {
  me: () => get<Me>('/auth/me'),
  logout: () => get<{ ok: true }>('/auth/logout'),
  accounts: (portal: PortalKey) => get<{ rows: AccountRow[] }>(`/api/accounts?portal=${portal}`),
  usage: (portal: PortalKey, start: string, end: string, userIds?: number[]) =>
    get<UsageResponse>(`/api/usage?portal=${portal}&${range(start, end)}${ids(userIds)}`),
  upsells: (portal: PortalKey, start: string, end: string, granularity: UpsellGranularity, userIds?: number[]) =>
    get<UpsellTrends>(`/api/upsells?portal=${portal}&${range(start, end)}&granularity=${granularity}${ids(userIds)}`),
  tickets: (refresh = false) => get<TicketsBoard>(`/api/tickets${refresh ? '?refresh=1' : ''}`),
  ticketAttachments: (ticketId: string, refresh = false) =>
    get<{ attachments: TicketAttachment[] }>(`/api/tickets/${ticketId}/attachments${refresh ? '?refresh=1' : ''}`),
  oneTimeLink: (portal: PortalKey, userId: number) =>
    get<{ url: string }>(`/api/one-time-link?portal=${portal}&userId=${userId}`),
  magicLoginUrl: (portal: PortalKey, userId: number) => `/api/magic-login?portal=${portal}&userId=${userId}`,
};
